import {
  PDF_CAP_HEIGHT_RATIO,
  PT_TO_MM,
  TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM,
  TWO_COLUMN_SECTION_LINE_TO_BODY_GAP_MM,
  TWO_COLUMN_SECTION_RULE_THICKNESS_MM,
  TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM,
} from './resumeExportPdfConfig'

/** Section header vertical split: SKILLS text → rule → body fits in one `mb-6` band. */
export function twoColumnSectionHeaderGapsMm(): {
  titleToLineMm: number
  ruleThicknessMm: number
  lineToBodyMm: number
  totalTitleToBodyMm: number
} {
  return {
    titleToLineMm: TWO_COLUMN_SECTION_TITLE_TO_LINE_GAP_MM,
    ruleThicknessMm: TWO_COLUMN_SECTION_RULE_THICKNESS_MM,
    lineToBodyMm: TWO_COLUMN_SECTION_LINE_TO_BODY_GAP_MM,
    totalTitleToBodyMm: TWO_COLUMN_COLUMN_BODY_TOP_GAP_MM,
  }
}

/** Collapse whitespace inside a paragraph; does not merge separate blocks. */
export function cleanResumeParagraphChunk(text: string): string {
  return String(text || '')
    .replace(/\u00ad/g, '')
    .replace(/\u200b/g, '')
    .replace(/[\u2022\u25CF\uF0B7]/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim()
}

/**
 * Split summary/body like preview `whitespace-pre-wrap`: blank lines = new paragraph.
 * If there is only one block but multiple single newlines, each line becomes its own paragraph.
 */
export function splitResumeParagraphs(text: string): string[] {
  const raw = String(text ?? '').replace(/\r\n/g, '\n').trim()
  if (!raw) return []

  const byBlank = raw.split(/\n\s*\n+/)
  if (byBlank.length > 1) {
    return byBlank.map(cleanResumeParagraphChunk).filter(Boolean)
  }

  if (raw.includes('\n')) {
    return raw.split('\n').map(cleanResumeParagraphChunk).filter(Boolean)
  }

  const single = cleanResumeParagraphChunk(raw)
  return single ? [single] : []
}

/** jsPDF `text` uses baseline; align cap-tops of two font sizes on the same row. */
export function baselineForAlignedCapTops(
  referenceBaselineMm: number,
  referenceFontSizePt: number,
  targetFontSizePt: number,
  capRatio = PDF_CAP_HEIGHT_RATIO
): number {
  const refCapMm = referenceFontSizePt * PT_TO_MM * capRatio
  const targetCapMm = targetFontSizePt * PT_TO_MM * capRatio
  const referenceTopMm = referenceBaselineMm - refCapMm
  return referenceTopMm + targetCapMm
}
